import { useState, useEffect } from 'react';
import { useSttChunkRecorder } from '@/hooks/useSttChunkRecorder';

export function SttTestPage() {
  const [interviewId, setInterviewId] = useState('test-interview-123');
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, `[${timestamp}] ${message}`]);
  };

  const {
    startListening,
    doneListening,
    isListening,
    error,
    lastChunkText,
    finalTranscript,
  } = useSttChunkRecorder({
    interviewId,
  });

  useEffect(() => {
    if (lastChunkText) {
      addLog(`Chunk text received: ${lastChunkText}`);
    }
  }, [lastChunkText]);

  useEffect(() => {
    if (finalTranscript) {
      addLog(`Final transcript received: ${finalTranscript}`);
    }
  }, [finalTranscript]);

  useEffect(() => {
    if (error) {
      addLog(`Error: ${error}`);
    }
  }, [error]);

  const handleStart = () => {
    addLog('Starting listening...');
    startListening();
  };

  const handleStop = () => {
    addLog('Stopping listening...');
    doneListening();
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold">STT Chunk Recorder Test</h1>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Interview ID</label>
            <input
              type="text"
              value={interviewId}
              onChange={(e) => setInterviewId(e.target.value)}
              disabled={isListening}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex gap-4">
            <button
              onClick={handleStart}
              disabled={isListening}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                isListening
                  ? 'bg-gray-600 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              Start Listening
            </button>
            <button
              onClick={handleStop}
              disabled={!isListening}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                !isListening
                  ? 'bg-gray-600 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              Stop Listening
            </button>
          </div>

          <div className="flex items-center gap-2">
            <div
              className={`w-3 h-3 rounded-full ${
                isListening ? 'bg-red-500 animate-pulse' : 'bg-gray-500'
              }`}
            />
            <span>{isListening ? 'Recording...' : 'Not recording'}</span>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-900/50 border border-red-700 rounded-lg">
            <p className="font-medium">Error:</p>
            <p>{error}</p>
          </div>
        )}

        {lastChunkText && (
          <div className="p-4 bg-blue-900/50 border border-blue-700 rounded-lg">
            <p className="font-medium">Last Chunk Text:</p>
            <p>{lastChunkText}</p>
          </div>
        )}

        {finalTranscript && (
          <div className="p-4 bg-green-900/50 border border-green-700 rounded-lg">
            <p className="font-medium">Final Transcript:</p>
            <p>{finalTranscript}</p>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium">Logs</h2>
            <button
              onClick={() => setLogs([])}
              className="text-sm text-gray-400 hover:text-white"
            >
              Clear
            </button>
          </div>
          <div className="h-64 overflow-y-auto bg-gray-800 border border-gray-700 rounded-lg p-4 font-mono text-sm">
            {logs.length === 0 ? (
              <p className="text-gray-500">No logs yet...</p>
            ) : (
              logs.map((log, i) => (
                <div key={i} className="text-gray-300">
                  {log}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SttTestPage;
